import type { StackProps } from '@chakra-ui/react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { MaskIcon } from '@ui/components/MaskIcon';
import { useUpdateUserProfile, useUserProfile } from '@ui/hooks/api/useUser';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useIsMyAccount } from '../../useIsMyAccount';

interface FormDataType {
  name: string;
}

export const Nickname = (props: StackProps) => {
  const isMyAccount = useIsMyAccount();
  const [isInputShown, setIsInputShown] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const userProfileRet = useUserProfile();
  const updateUserProfileMutation = useUpdateUserProfile();

  const methods = useForm<FormDataType>({
    mode: 'all',
    values: {
      name: userProfileRet.data?.name ?? '',
    },
  });

  const handleSubmit: SubmitHandler<FormDataType> = async ({ name }) => {
    setIsUpdating(true);
    try {
      await updateUserProfileMutation.mutateAsync({
        name,
      });
      await userProfileRet.refetch();
      setIsInputShown(false);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box {...props}>
      {!isInputShown && (
        <HStack
          minH="40px"
          cursor="pointer"
          gap="4px"
          onClick={() => {
            if (!isMyAccount) return;
            setIsInputShown(true);
          }}
        >
          <Text textStyle="body1Bold" lineClamp={1} wordBreak="break-all">
            {userProfileRet.data?.name || '--'}
          </Text>
          <MaskIcon
            hidden={!isMyAccount}
            flexShrink="0"
            icon="url(/masks/edit.svg)"
            boxSize="16px"
          />
        </HStack>
      )}
      {isInputShown && (
        <HStack
          gap="0px"
          as="form"
          onSubmit={methods.handleSubmit(handleSubmit)}
        >
          <Flex flexDirection="column">
            <Flex alignItems="center">
              <Input
                {...methods.register('name', {
                  maxLength: { value: 36, message: 'Maximum 36 characters' },
                  required: 'Nickname is required',
                })}
                flex="1"
                variant="flushed"
                placeholder="Fill your nickname!"
                autoFocus
              />
              <Button
                variant="ghost"
                boxSize="30px"
                p="0"
                type="submit"
                disabled={isUpdating || !methods.formState.isValid}
              >
                {isUpdating ? (
                  <Spinner boxSize="16px" />
                ) : (
                  <MaskIcon icon="url(/masks/check.svg)" boxSize="16px" />
                )}
              </Button>
              <Button
                variant="ghost"
                boxSize="30px"
                p="0"
                onClick={() => {
                  setIsInputShown(false);
                }}
              >
                <MaskIcon icon="url(/masks/close.svg)" boxSize="16px" />
              </Button>
            </Flex>
            <Flex>
              {!!methods.formState.errors.name && (
                <Text color="state.error" textStyle="caption" px={4}>
                  {methods.formState.errors.name.message}
                </Text>
              )}
            </Flex>
          </Flex>
        </HStack>
      )}
    </Box>
  );
};
